import { useState } from 'react';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuthHeaders } from './useAuthHeaders';

interface UploadFileParams {
    category: string;
    fileName: string;
    file: File;
}

export const useUploadFile = () => {
    const { settings: appSettings } = useAppSettings();
    const { getAuthHeaders } = useAuthHeaders();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const uploadFile = async ({ category, fileName, file }: UploadFileParams): Promise<void> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        try {
            const fileBytes = await file.arrayBuffer();

            const response = await fetch(
                `${appSettings.api}/UploadFile?category=${encodeURIComponent(category)}&fileName=${encodeURIComponent(fileName)}`,
                {
                    method: 'POST',
                    headers: getAuthHeaders({ 'Content-Type': 'application/octet-stream' }),
                    body: fileBytes,
                }
            );

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.statusText}`);
            }

            setUploadSuccess(true);
        } catch (error: any) {
            setUploadError(error.message || 'Failed to upload file');
            throw error;
        } finally {
            setIsUploading(false);
        }
    };

    const clearMessages = () => {
        setUploadError(null);
        setUploadSuccess(false);
    };

    return {
        uploadFile,
        isUploading,
        uploadError,
        uploadSuccess,
        clearMessages,
    };
};
